import { relativeLuminance } from '@/loginTone';

/**
 * Gets a picture the MD chose ready to be the sign-in picture.
 *
 * A photo straight off a phone is the usual case: 4000px across, 5 MB, lying
 * on its side until its camera details are read, and carrying where it was
 * taken. Here it is turned upright, shrunk to what the panel can show,
 * re-saved as a JPEG with nothing but the picture in it, and measured for how
 * bright it is where the panel's text will sit. The API checks the result
 * again, but only this side can decode a picture.
 */

/** The longest side kept: the panel is at most ~440px wide, so 2x and room to spare. */
export const MAX_SIDE = 1600;
/** Must match the API: the short side it refuses below. */
export const MIN_SIDE = 600;
/** Below this the picture is accepted, with a warning that it may look soft. */
export const SOFT_SIDE = 1000;
/** The API's limit is 1 MB; aim well under it so the sign-in screen loads fast. */
export const TARGET_BYTES = 600 * 1024;
/** Bigger than any camera photo; refusing earlier saves decoding a huge file. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** 50 megapixels: past this, decoding alone could exhaust a tablet's memory. */
export const MAX_PIXELS = 50_000_000;
/** The panel is close to square; beyond this much of the picture is cut off. */
export const WIDE_WARNING = 2.2;
/** Beyond this the short side cannot reach MIN_SIDE within the API's 2400px. */
export const MAX_ASPECT = 4;
/** Painted under the picture, so a transparent PNG goes dark, not black-and-jagged. */
const BACKDROP = '#1c1e1d';
const QUALITIES = [0.85, 0.78, 0.7, 0.6];

export interface PreparedPicture {
  blob: Blob;
  width: number;
  height: number;
  tint: string;
  lumaTop: number;
  lumaMid: number;
  lumaBottom: number;
  palette: string;
}

export type PrepareResult =
  | { ok: true; picture: PreparedPicture; warnings: string[] }
  | { ok: false; error: string };

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPTED_EXT = /\.(jpe?g|png|webp)$/i;
const HEIC = /\.(heic|heif)$/i;

/** What a file input should offer. */
export const ACCEPT_ATTR = 'image/jpeg,image/png,image/webp';

/** Why this file cannot be used, judged before decoding it; null if it may be. */
export function fileProblem(file: { name: string; type: string; size: number }): string | null {
  if (file.type === 'image/heic' || file.type === 'image/heif' || HEIC.test(file.name)) {
    return 'This is an iPhone (HEIC) picture, which browsers cannot open. Save or share it as a JPEG and choose that instead.';
  }
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) {
    return 'Drawings (SVG) cannot be used. Choose a photo: JPEG, PNG or WebP.';
  }
  const typeOk = file.type ? ACCEPTED.includes(file.type) : ACCEPTED_EXT.test(file.name);
  if (!typeOk) return 'Choose a JPEG, PNG or WebP picture.';
  if (file.size === 0) return 'This file is empty.';
  if (file.size > MAX_FILE_BYTES) return 'This file is over 20 MB. Choose a smaller picture.';
  return null;
}

/**
 * Why a picture of this size cannot be used, or the warnings it comes with.
 * Separate from decoding so it can be tested on its own.
 */
export function sizeCheck(width: number, height: number): { error: string } | { warnings: string[] } {
  if (width * height > MAX_PIXELS) {
    return { error: 'This picture is too large to open here. Choose one under 50 megapixels.' };
  }
  const short = Math.min(width, height);
  const long = Math.max(width, height);
  if (short < MIN_SIDE) {
    return {
      error: `This picture is too small (${width} × ${height}). It needs to be at least ${MIN_SIDE} pixels on its shorter side.`,
    };
  }
  if (long / short > MAX_ASPECT) {
    return { error: 'This picture is too long and narrow for the sign-in panel. Crop it closer to a square first.' };
  }
  const warnings: string[] = [];
  if (short < SOFT_SIDE) {
    warnings.push(`At ${width} × ${height} it may look slightly soft on sharp screens.`);
  }
  if (long / short > WIDE_WARNING) {
    warnings.push(
      'The panel is close to square, so much of this picture’s ' +
        (width > height ? 'left and right' : 'top and bottom') +
        ' will be cut off. Check the preview.',
    );
  }
  return { warnings };
}

/**
 * The size to save at: the long side at most MAX_SIDE, but never so small
 * that the short side falls under MIN_SIDE (a wide panorama keeps more width).
 */
export function targetSize(width: number, height: number): { width: number; height: number } {
  const short = Math.min(width, height);
  const long = Math.max(width, height);
  let scale = Math.min(1, MAX_SIDE / long);
  if (short * scale < MIN_SIDE) scale = Math.min(1, MIN_SIDE / short);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

const hex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

/** The 95th-percentile value: the bright parts, without one stray white pixel deciding. */
export function percentile95(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]!;
}

/** The average colour, kept in hue but darkened to about 8% lightness. */
export function darkTint(r: number, g: number, b: number): string {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const [rr, gg, bb] = [r / 255, g / 255, b / 255];
    h = max === rr ? (gg - bb) / d + (gg < bb ? 6 : 0) : max === gg ? (bb - rr) / d + 2 : (rr - gg) / d + 4;
    h /= 6;
  }
  // A little colour, not a lot: a saturated shade would recolour the picture.
  s = Math.min(s, 0.35);
  const L = 0.08;
  const q = L < 0.5 ? L * (1 + s) : L + s - L * s;
  const p = 2 * L - q;
  const channel = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return hex(channel(h + 1 / 3) * 255, channel(h) * 255, channel(h - 1 / 3) * 255);
}

/**
 * Shapes (height ÷ width) the sign-in panel takes: from the stacked layout on
 * a narrow tablet (wide and short) to the side-by-side card with a tall form
 * beside it. The picture is cropped from the centre to each.
 */
export const PANEL_SHAPES = [0.6, 0.85, 1.05, 1.3, 1.5];
/** The usual one: half of the 880px card, a little taller than wide. */
const USUAL_SHAPE = 1.05;
/**
 * Where the panel's text can sit, as fractions of its height: generous, so a
 * long company name over two lines or a tall logo is still covered. The two
 * overlap; the middle is measured on its own for the gap between them.
 */
export const TOP_TEXT = [0, 0.55] as const;
export const MIDDLE = [0.4, 0.7] as const;
export const BOTTOM_TEXT = [0.55, 1] as const;

/** The part of the picture a panel of `shape` shows, cropped from the centre. */
export function visibleArea(width: number, height: number, shape: number) {
  if (height / width > shape) {
    const h = width * shape;
    return { x0: 0, y0: (height - h) / 2, x1: width, y1: (height + h) / 2 };
  }
  const w = height / shape;
  return { x0: (width - w) / 2, y0: 0, x1: (width + w) / 2, y1: height };
}

/**
 * Measures a small copy of the picture where the panel's text will sit over
 * it: the brightness of the brightest parts behind the company name and
 * tagline (top), the empty middle and the paragraph (bottom), taking the
 * brightest of every panel shape, since the crop changes with the window. Also
 * the average colours for the loading placeholder, and the shade's tint.
 * `data` is RGBA, as canvas getImageData returns it.
 */
export function analysePixels(
  data: ArrayLike<number>,
  width: number,
  height: number,
): Pick<PreparedPicture, 'tint' | 'lumaTop' | 'lumaMid' | 'lumaBottom' | 'palette'> {
  function region(x0: number, y0: number, x1: number, y1: number) {
    const lumas: number[] = [];
    const sum = [0, 0, 0];
    const xs = Math.max(0, Math.floor(x0));
    const xe = Math.min(width, Math.max(xs + 1, Math.ceil(x1)));
    const ys = Math.max(0, Math.floor(y0));
    const ye = Math.min(height, Math.max(ys + 1, Math.ceil(y1)));
    for (let y = ys; y < ye; y++) {
      for (let x = xs; x < xe; x++) {
        const i = (y * width + x) * 4;
        const r = data[i]!;
        const g = data[i + 1]!;
        const b = data[i + 2]!;
        lumas.push(relativeLuminance(r, g, b));
        sum[0]! += r;
        sum[1]! += g;
        sum[2]! += b;
      }
    }
    const n = Math.max(1, lumas.length);
    return {
      luma: percentile95(lumas),
      rgb: [sum[0]! / n, sum[1]! / n, sum[2]! / n] as const,
    };
  }

  function bands(shape: number) {
    const a = visibleArea(width, height, shape);
    const h = a.y1 - a.y0;
    return [TOP_TEXT, MIDDLE, BOTTOM_TEXT].map(([from, to]) =>
      region(a.x0, a.y0 + h * from, a.x1, a.y0 + h * to),
    );
  }

  const measured = PANEL_SHAPES.map(bands);
  const brightest = (i: number) =>
    Math.round(Math.max(...measured.map((m) => m[i]!.luma)) * 1000) / 1000;
  const usual = bands(USUAL_SHAPE);
  const whole = region(0, 0, width, height);

  return {
    tint: darkTint(...whole.rgb),
    lumaTop: brightest(0),
    lumaMid: brightest(1),
    lumaBottom: brightest(2),
    palette: usual.map((b) => hex(...b.rgb)).join(','),
  };
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

/**
 * Decodes the file the right way up. createImageBitmap reads the camera's
 * orientation when asked; an <img> does so on its own in current browsers.
 */
async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch {
      // Older Safari: no options argument. Fall through to <img>.
    }
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    close: () => URL.revokeObjectURL(url),
  };
}

function canvas(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return [c, ctx];
}

/**
 * Shrinks in halves until close to the target, then the last step: a single
 * big jump looks grainy in browsers whose smoothing reads only a few pixels.
 */
function resize(source: CanvasImageSource, from: { width: number; height: number }, to: { width: number; height: number }) {
  let current: CanvasImageSource = source;
  let w = from.width;
  let h = from.height;
  while (w / 2 >= to.width && h / 2 >= to.height) {
    const [c, ctx] = canvas(Math.round(w / 2), Math.round(h / 2));
    ctx.fillStyle = BACKDROP;
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(current, 0, 0, c.width, c.height);
    current = c;
    w = c.width;
    h = c.height;
  }
  const [c, ctx] = canvas(to.width, to.height);
  ctx.fillStyle = BACKDROP;
  ctx.fillRect(0, 0, to.width, to.height);
  ctx.drawImage(current, 0, 0, to.width, to.height);
  return { canvas: c, ctx };
}

function toJpeg(c: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => c.toBlob(resolve, 'image/jpeg', quality));
}

export async function preparePicture(file: File): Promise<PrepareResult> {
  const problem = fileProblem(file);
  if (problem) return { ok: false, error: problem };

  let decoded: Decoded;
  try {
    decoded = await decode(file);
  } catch {
    return {
      ok: false,
      error: 'This picture could not be opened. It may be damaged, or in a format this browser cannot read. Try saving it as a JPEG.',
    };
  }

  try {
    const checked = sizeCheck(decoded.width, decoded.height);
    if ('error' in checked) return { ok: false, error: checked.error };

    const size = targetSize(decoded.width, decoded.height);
    const { canvas: out } = resize(decoded.source, decoded, size);

    let blob: Blob | null = null;
    for (const q of QUALITIES) {
      blob = await toJpeg(out, q);
      if (!blob || blob.size <= TARGET_BYTES) break;
    }
    if (!blob) return { ok: false, error: 'This picture could not be prepared. Try a different one.' };
    if (blob.size > TARGET_BYTES * 1.6) {
      return { ok: false, error: 'This picture has too much fine detail to save small enough. Try a different one.' };
    }

    // A small copy, 64px on its short side, is plenty to judge brightness
    // and quick to read.
    const sample = 64 / Math.min(size.width, size.height);
    const sw = Math.max(1, Math.round(size.width * sample));
    const sh = Math.max(1, Math.round(size.height * sample));
    const { ctx } = resize(out, size, { width: sw, height: sh });
    const tone = analysePixels(ctx.getImageData(0, 0, sw, sh).data, sw, sh);

    return {
      ok: true,
      picture: { blob, width: size.width, height: size.height, ...tone },
      warnings: checked.warnings,
    };
  } catch {
    return { ok: false, error: 'This picture could not be prepared. Try a different one.' };
  } finally {
    decoded.close();
  }
}
